import { Bell, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSeriesInterest } from "@/hooks/useSeriesInterest";
import { useAuth } from "@/components/auth/AuthProvider";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { ExperienceSection } from "@/types/experience-section";
import { capture as posthogCapture } from "@/services/posthog";

type Props = {
  section: ExperienceSection;
};

const SeriesInterestButton = ({ section }: Props) => {
  const { isAuthenticated } = useAuth();
  const { isInterested, register, unregister, isLoading } = useSeriesInterest();
  const { toast } = useToast();
  const navigate = useNavigate();

  if (!section.enable_interest_signup || section.section_type === "archived") {
    return null;
  }

  const interested = isInterested(section.id);
  const busy = register.isPending || unregister.isPending;

  const handleClick = async () => {
    if (!isAuthenticated) {
      navigate(`/auth?redirectTo=/interest/${section.slug}`);
      return;
    }

    try {
      if (interested) {
        await unregister.mutateAsync(section.id);
        posthogCapture("series_interest_toggled", {
          series_slug: section.slug,
          interested: false,
        });
        toast({
          title: "Notifications turned off",
          description: `We won't email you about new ${section.title} events.`,
        });
      } else {
        await register.mutateAsync(section.id);
        posthogCapture("series_interest_toggled", {
          series_slug: section.slug,
          interested: true,
        });
        toast({
          title: "You're on the list",
          description: `We'll email you when new ${section.title} events launch.`,
        });
      }
    } catch (e: any) {
      toast({
        title: "Something went wrong",
        description: e?.message ?? "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleClick}
      disabled={busy || isLoading}
      className={interested
        ? "h-8 shrink-0 gap-1.5 rounded-full bg-success/15 px-3 text-xs text-success hover:bg-success/20 hover:text-success"
        : "h-8 shrink-0 gap-1.5 rounded-full bg-secondary/50 px-3 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground"
      }
      aria-label={interested ? `Turn off ${section.title} notifications` : `Notify me about ${section.title}`}
    >
      {interested ? <BellRing className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}
      <span>{interested ? "Notifications on" : "Notify me"}</span>
    </Button>
  );
};

export default SeriesInterestButton;
